const toNumberOrNull = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const toNonEmptyString = (value) => {
  if (value == null) return null;
  const normalized = `${value}`.trim();
  return normalized.length ? normalized : null;
};

export const ensureLatLng = (node = {}) => {
  const lat = toNumberOrNull(
    node?.coords?.[0] ??
      node?.coordinates?.[0] ??
      node?.location?.coords?.[0] ??
      node?.location?.coordinates?.[0] ??
      node?.latLng?.lat ??
      node?.latLng?.latitude ??
      node?.location?.latLng?.lat ??
      node?.location?.latLng?.latitude ??
      node?.location?.lat ??
      node?.location?.latitude ??
      node?.lat ??
      node?.latitude,
  );
  const lng = toNumberOrNull(
    node?.coords?.[1] ??
      node?.coordinates?.[1] ??
      node?.location?.coords?.[1] ??
      node?.location?.coordinates?.[1] ??
      node?.latLng?.lng ??
      node?.latLng?.longitude ??
      node?.location?.latLng?.lng ??
      node?.location?.latLng?.longitude ??
      node?.location?.lng ??
      node?.location?.longitude ??
      node?.lng ??
      node?.longitude,
  );
  return { lat, lng };
};

// Treat near-identical coordinates as the same node (loop detection).
const sameLatLng = (a, b, tol = 1e-5) =>
  Number.isFinite(a?.lat) &&
  Number.isFinite(a?.lng) &&
  Number.isFinite(b?.lat) &&
  Number.isFinite(b?.lng) &&
  Math.abs(a.lat - b.lat) <= tol &&
  Math.abs(a.lng - b.lng) <= tol;

const resolveNodeZoneId = (node = {}) =>
  node?.zoneId ?? node?.zone_id ?? node?.geometry?.zoneId ?? null;

const sameNodeIdentity = (a, b) => {
  if (!a || !b) return false;

  const aId = toNonEmptyString(a?.id);
  const bId = toNonEmptyString(b?.id);
  if (aId && bId && aId === bId) return true;

  const aZoneId = toNonEmptyString(resolveNodeZoneId(a));
  const bZoneId = toNonEmptyString(resolveNodeZoneId(b));
  if (aZoneId && bZoneId && aZoneId === bZoneId) return true;

  const aPlaceId = toNonEmptyString(a?.placeId);
  const bPlaceId = toNonEmptyString(b?.placeId);
  if (aPlaceId && bPlaceId && aPlaceId === bPlaceId) return true;

  return false;
};

const isOriginLikeNode = (node = {}) => {
  const role = resolveNodeRole(node);
  return role === 'origin' || role === 'start';
};

const isTypedTourNode = (node = {}) => {
  const role = resolveNodeRole(node);
  return role === 'origin' || role === 'checkpoint' || role === 'destination' || role === 'start' || role === 'end';
};

const hasTypedTourNodes = (nodes = []) =>
  Array.isArray(nodes) && nodes.some((node) => isTypedTourNode(node));

const hasRouteBoundaryNode = (nodes = []) =>
  Array.isArray(nodes) &&
  nodes.some((node) => {
    const nodeType = `${node?.type ?? node?.kind ?? ''}`.toLowerCase().trim();
    return nodeType === 'origin' || nodeType === 'start' || nodeType === 'destination' || nodeType === 'end';
  });

const checkpointIdentityKey = (node = {}) => {
  const zoneId = toNonEmptyString(node?.zoneId ?? node?.zone_id ?? node?.geometry?.zoneId);
  if (zoneId) return `zone:${zoneId}`;

  const placeId = toNonEmptyString(node?.placeId);
  if (placeId) return `place:${placeId}`;

  const { lat, lng } = ensureLatLng(node);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `latlng:${lat.toFixed(5)},${lng.toFixed(5)}`;
  }

  const address = toNonEmptyString(node?.address ?? node?.name ?? node?.label);
  if (address) return `text:${address.toLowerCase()}`;

  const id = toNonEmptyString(node?.id);
  if (id) return `id:${id}`;

  return null;
};

const dedupeCheckpointNodes = (nodes = []) => {
  if (!Array.isArray(nodes) || !nodes.length) return [];
  const seen = new Set();
  const deduped = [];
  nodes.forEach((node) => {
    const key = checkpointIdentityKey(node);
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    deduped.push(node);
  });
  return deduped;
};

const ROLE_TYPES = new Set(['origin', 'start', 'destination', 'end', 'checkpoint']);

const resolveNodeRole = (node = {}) => {
  const fromType = `${node?.type ?? ''}`.toLowerCase().trim();
  if (fromType) return fromType;
  const fromKind = `${node?.kind ?? ''}`.toLowerCase().trim();
  // Only treat top-level kind as a role if it's a known role value, not a geometry shape name.
  if (fromKind && ROLE_TYPES.has(fromKind)) return fromKind;
  return '';
};

const normalizeGeometry = (node = {}) => {
  const geo = node.geometry || {};
  // Exclude node.kind from geometry detection — it may hold a role value like 'destination'.
  const rawType = geo.kind || geo.type || node.geometryType || node.shapeMode;
  const type = rawType ? String(rawType).toLowerCase() : null;
  const zoneId = geo.zoneId ?? node.zoneId ?? node.zone_id ?? null;
  const isZone = type === 'zone' || geo.isZone || zoneId != null;
  if (type === 'polygon' || type === 'zone') {
    const pts = Array.isArray(geo.points) ? geo.points : [];
    return { type: 'polygon', points: pts, zoneId, isZone };
  }
  if (type === 'crossline') {
    // Resolve to radians: prefer angleRad/handleBearing (already radians),
    // otherwise convert angleDeg (degrees → radians) from the persisted API value.
    const resolvedAngleRad =
      geo.angleRad != null
        ? Number(geo.angleRad)
        : geo.handleBearing != null
          ? Number(geo.handleBearing)
          : geo.angleDeg != null
            ? Number(geo.angleDeg) * (Math.PI / 180)
            : null;
    const angleRad = Number.isFinite(resolvedAngleRad) ? resolvedAngleRad : null;
    return {
      type: 'crossline',
      points: Array.isArray(geo.points) ? geo.points : [],
      radiusMeters: geo.radiusMeters ?? geo.radius ?? node.radiusMeters ?? node.radius ?? 75,
      angleRad,
      handleBearing: angleRad,
    };
  }
  // default circle
  const radius = geo.radiusMeters ?? geo.radius ?? node.radiusMeters ?? node.radius ?? 75;
  return { type: 'circle', radiusMeters: radius };
};

const fallbackLabel = (node, idx) => {
  if (typeof node?.label === 'string' && node.label.trim().length) return node.label;
  if (typeof node?.name === 'string' && node.name.trim().length) return node.name;
  return `Checkpoint ${idx + 1}`;
};

export const toCheckpoint = (node, idx) => {
  const { lat, lng } = ensureLatLng(node);
  const radius = toNumberOrNull(node?.radius);
  const etaSeconds = toNumberOrNull(node?.etaSeconds);
  const handleBearing = toNumberOrNull(node?.handleBearing);
  const stopIndex = Number.isInteger(node?.stopIndex) ? node.stopIndex : idx + 1;
  const timeWindowMinutes = toNumberOrNull(node?.bonusTime);
  const stopTimeMinutes = toNumberOrNull(node?.waitTime);
  const modeRaw = (node?.travelMode || node?.mode || node?.leg?.travelMode || '')
    .toString()
    .toUpperCase();
  const mode =
    modeRaw === 'WALK' || modeRaw === 'WALKING'
      ? 'WALKING'
      : modeRaw === 'DRIVE' || modeRaw === 'DRIVING'
        ? 'DRIVING'
        : null;

  const zoneId = node?.zoneId ?? node?.zone_id ?? node?.geometry?.zoneId ?? null;
  return {
    id: node?.id ?? `node-${idx}`,
    label: fallbackLabel(node, idx),
    address: node?.address || node?.label || node?.name || '',
    lat: lat ?? '',
    lng: lng ?? '',
    radiusMeters: radius ?? 75,
    stopTimeMinutes: stopTimeMinutes ?? 0,
    timeWindowMinutes: timeWindowMinutes ?? 0,
    timeWindowSeconds: toNumberOrNull(node?.timeWindowSeconds),
    etaSeconds: etaSeconds ?? null,
    stopIndex,
    handleBearing: handleBearing ?? 0,
    placeId: node?.placeId || '',
    isCheckpoint: true,
    kind: resolveNodeRole(node) || 'checkpoint',
    coordinates:
      Array.isArray(node?.coords) && node.coords.length >= 2
        ? node.coords
        : lat != null && lng != null
          ? [lat, lng]
          : null,
    geometry: normalizeGeometry(node),
    zoneId,
    mode: mode || 'DRIVING',
  };
};

export const splitTourNodes = (nodes = [], options = {}) => {
  const forceLooped = options?.forceLooped === true;
  const list = Array.isArray(nodes) ? nodes.filter(Boolean) : [];
  if (!list.length) return { originNode: null, checkpoints: [] };

  const originIndex = list.findIndex((node) => {
    const role = resolveNodeRole(node);
    return role === 'origin' || role === 'start';
  });
  const resolvedOriginIndex = originIndex >= 0 ? originIndex : 0;
  const originNode = list[resolvedOriginIndex];
  const originLatLng = ensureLatLng(originNode);
  const lastIndex = list.length - 1;
  const lastNode = list[lastIndex];
  const lastNodeRole = resolveNodeRole(lastNode);
  const lastLooksLikeTerminalNode =
    lastNodeRole === 'destination' || lastNodeRole === 'origin' || lastNodeRole === 'end';
  const detectedLooped =
    lastIndex > resolvedOriginIndex &&
    (sameLatLng(originLatLng, ensureLatLng(lastNode)) ||
      sameNodeIdentity(originNode, lastNode) ||
      (lastLooksLikeTerminalNode && sameNodeIdentity(originNode, lastNode)));

  const shouldDropTerminalNode =
    lastIndex > resolvedOriginIndex &&
    (detectedLooped || (forceLooped && lastLooksLikeTerminalNode));

  const isLooped = forceLooped || detectedLooped;
  const checkpointSource = list.filter(
    (_, idx) => idx !== resolvedOriginIndex && (!shouldDropTerminalNode || idx !== lastIndex),
  );

  const dedupedCheckpointSource = dedupeCheckpointNodes(checkpointSource);
  const checkpoints = dedupedCheckpointSource.map((node, idx) => toCheckpoint(node, idx));

  return {
    originNode: originNode || null,
    checkpoints,
    isLooped,
  };
};

const resolveOriginNode = (tour) =>
  tour?.originNode ||
  tour?.origin ||
  tour?.start ||
  tour?.startLocation ||
  tour?.start_node ||
  tour?.startNode ||
  null;

export const deriveCheckpointsFromTour = (tour) => {
  if (!tour) return { originNode: null, checkpoints: [] };
  const forceLooped = tour?.isLooped === true;
  // Prefer canonical route nodes when they include explicit boundary node types.
  // This avoids treating plain checkpoint arrays as if index 0 were origin.
  if (hasTypedTourNodes(tour?.nodes)) {
    return splitTourNodes(tour.nodes, { forceLooped });
  }
  if (hasTypedTourNodes(tour?.stops) && hasRouteBoundaryNode(tour?.stops)) {
    return splitTourNodes(tour.stops, { forceLooped });
  }
  if (hasTypedTourNodes(tour?.checkpoints) && hasRouteBoundaryNode(tour?.checkpoints)) {
    return splitTourNodes(tour.checkpoints, { forceLooped });
  }

  const checkpoints = Array.isArray(tour?.checkpoints)
    ? tour.checkpoints
    : Array.isArray(tour?.stops)
      ? tour.stops
      : Array.isArray(tour?.nodes) && !hasTypedTourNodes(tour.nodes)
        ? tour.nodes
      : [];
  const originNode = resolveOriginNode(tour);
  const originLatLng = ensureLatLng(originNode);
  const sanitizedCheckpoints = (Array.isArray(checkpoints) ? checkpoints : []).filter((node) => {
    if (!node) return false;
    if (isOriginLikeNode(node)) return false;
    if (originNode && (sameNodeIdentity(originNode, node) || sameLatLng(originLatLng, ensureLatLng(node)))) {
      return false;
    }
    return true;
  });
  const dedupedSanitizedCheckpoints = dedupeCheckpointNodes(sanitizedCheckpoints);

  return {
    originNode,
    checkpoints: dedupedSanitizedCheckpoints,
    isLooped: forceLooped,
  };
};
